import { HTTP_TIMEOUT_ERROR_NAME } from '@federa/const/http-error-codes';
import { ERROR_REQUEST_TIMED_OUT } from '@federa/const/http-error-values';
import type { HttpAbortSetup } from '@federa/types/http';

/** Returns the named `HttpAbortSetup` bundle rather than an inline structural twin of it: that interface's own docstring
 * declares itself "the bundle the `setupAbortController` seam returns", so the two were one contract spelled twice. */
export function setupAbortController(options: RequestInit, timeoutMs: number): HttpAbortSetup {
	const controller = new AbortController();
	const { signal: originalSignal, ...restOptions } = options;
	let abortListener: (() => void) | undefined;
	let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

	if (originalSignal) {
		if (originalSignal.aborted) {
			controller.abort();
		} else {
			abortListener = () => controller.abort();
			originalSignal.addEventListener('abort', abortListener);
		}
	}

	const timeoutPromise = new Promise<never>((_, reject) => {
		timeoutHandle = setTimeout(() => {
			controller.abort();
			const timeoutError = new Error(ERROR_REQUEST_TIMED_OUT);
			timeoutError.name = HTTP_TIMEOUT_ERROR_NAME;
			reject(timeoutError);
		}, timeoutMs);
	});

	const cleanup = (): void => {
		clearTimeout(timeoutHandle);
		if (abortListener && originalSignal) {
			originalSignal.removeEventListener('abort', abortListener);
		}
	};

	return { controller, signalledInit: { ...restOptions, signal: controller.signal }, originalSignal, timeoutPromise, cleanup };
}
